import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarX2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const MANAGE_ROLES = ["staffing_coordinator", "operations", "system_administrator"];
const TYPES = ["Closed", "No students", "Early release", "First/last day", "Snow day", "Emergency", "Snow make-up (tentative)", "Other"];
const SKIP_TYPES = ["Closed", "No students", "Snow day", "Emergency"];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EMPTY = { client_id: "", start_date: "", end_date: "", type: "Closed", description: "", note: "", source: "manual", recurring_weekday: "" };
const fmt = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

function ClosureForm({ clients, initial, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, recurring_weekday: initial?.recurring_weekday ?? "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, recurring_weekday: form.recurring_weekday === "" ? null : form.recurring_weekday };
      const response = initial?.id ? await api.put(`/attendance/closures/${initial.id}`, payload) : await api.post("/attendance/closures", payload);
      onSaved(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="evaluation-action-form closure-form" data-testid="closure-form" onSubmit={submit}>
      <div className="drawer-form-grid">
        <label>Client<select data-testid="closure-client" required value={form.client_id} onChange={set("client_id")}><option value="">Choose a client</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
        <label>Type<select data-testid="closure-type" value={form.type} onChange={set("type")}>{TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
        <label>First day<input data-testid="closure-start" required type="date" value={form.start_date} onChange={set("start_date")} /></label>
        <label>Last day<input data-testid="closure-end" type="date" value={form.end_date} onChange={set("end_date")} /></label>
        <label>Description<input data-testid="closure-description" maxLength={300} placeholder="Winter break, PD day, snow day…" value={form.description} onChange={set("description")} /></label>
        <label>Note (optional)<input data-testid="closure-note" maxLength={300} placeholder="Confirm closer to date…" value={form.note || ""} onChange={set("note")} /></label>
        <label>Recurring weekday (optional)<select data-testid="closure-weekday" value={form.recurring_weekday} onChange={set("recurring_weekday")}><option value="">Not recurring</option>{WEEKDAYS.map((day, index) => <option key={day} value={index}>{`Every ${day}`}</option>)}</select></label>
      </div>
      {error && <p className="form-message error-message" data-testid="closure-form-error">{error}</p>}
      <div className="verification-actions">
        <button type="submit" className="button button-primary" data-testid="closure-save" disabled={busy}>{initial?.id ? "Save changes" : "Add closure"}</button>
        <button type="button" className="button button-secondary" data-testid="closure-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function ClosuresPage() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [closures, setClosures] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [rows, clientRows] = await Promise.all([api.get("/attendance/closures"), api.get("/attendance/clients")]);
      setClosures(rows.data);
      setClients(clientRows.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function importCsv(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const summary = (await api.post("/attendance/closures/import", body, { headers: { "Content-Type": "multipart/form-data" } })).data;
      setNotice(`Import finished: ${summary.created} added, ${summary.updated} updated, ${summary.unchanged} unchanged${summary.unmatched_clients?.length ? `; kept under the school name (not a TAGForce client yet): ${summary.unmatched_clients.join(", ")}` : ""}${summary.errors.length ? `, ${summary.errors.length} row(s) skipped (${summary.errors.slice(0, 3).map((e) => `line ${e.line}: ${e.error}`).join("; ")})` : ""}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function remove(row) {
    if (!window.confirm(`Delete the ${row.type.toLowerCase()} on ${fmt(row.start_date)} for ${row.client_name}?`)) return;
    try {
      await api.delete(`/attendance/closures/${row.id}`);
      setNotice("Closure deleted.");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const rows = (closures || []).filter((row) => !clientFilter || (clientFilter.startsWith("name:") ? row.client_name === clientFilter.slice(5) : row.client_id === clientFilter));

  return (
    <div className="app-shell" data-testid="closures-shell">
      <AppSidebar activeItem="School closures" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page connecteam-page" data-testid="closures-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">OPERATIONS · ATTENDANCE</p>
              <h1>School closures</h1>
              <p className="page-subtitle">Days a client is closed or has no students are skipped by the clock-in check, as are days outside the first and last day of school. Early release days are still checked.</p>
            </div>
            {canManage && (
              <div className="page-intro-actions">
                <input ref={fileInput} data-testid="closures-import-input" type="file" accept=".csv,text/csv" hidden onChange={importCsv} />
                <button type="button" className="button button-secondary" data-testid="closures-import-button" onClick={() => fileInput.current?.click()}><Upload size={14} /> Import CSV</button>
                <button type="button" className="button button-primary" data-testid="closures-add-button" onClick={() => setEditing(editing && !editing.id ? null : {})}><Plus size={14} /> Add closure</button>
              </div>
            )}
          </section>
          {error && <p className="form-message error-message" data-testid="closures-error">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="closures-notice">{notice}</p>}
          {editing && <ClosureForm clients={clients} initial={editing} onCancel={() => setEditing(null)} onSaved={(row) => { setEditing(null); setNotice(`${row.client_name}: ${row.type} ${fmt(row.start_date)}${row.end_date !== row.start_date ? ` – ${fmt(row.end_date)}` : ""} saved.`); load(); }} />}
          <section className="connecteam-panel" data-testid="closures-panel">
            <div className="section-heading">
              <div><h2><CalendarX2 size={16} /> Closures</h2><p>{rows.length} on file{clientFilter ? " for this client" : ""}.</p></div>
              <div className="verification-toolbar">
                <label>Client<select data-testid="closures-client-filter" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}><option value="">All clients</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}{[...new Set((closures || []).filter((r) => !r.client_id).map((r) => r.client_name))].map((name) => <option key={name} value={`name:${name}`}>{name} (not a client yet)</option>)}</select></label>
              </div>
            </div>
            <div className="requests-table-panel">
              <table className="requests-table connecteam-table" data-testid="closures-table">
                <thead><tr><th>Client</th><th>Dates</th><th>Type</th><th>Description</th><th>Source</th>{canManage && <th />}</tr></thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} data-testid={`closure-row-${row.id}`}>
                      <td><strong>{row.client_name}</strong>{!row.client_id && <><br /><small className="pairing-meta" data-testid={`closure-unmatched-${row.id}`}>Not a TAGForce client yet · matched to the Connecteam site name</small></>}</td>
                      <td>{fmt(row.start_date)}{row.end_date && row.end_date !== row.start_date ? ` – ${fmt(row.end_date)}` : ""}{row.recurring_weekday !== null && row.recurring_weekday !== undefined && <><br /><small className="pairing-meta">Every {WEEKDAYS[row.recurring_weekday]}</small></>}</td>
                      <td><span className={`status-chip ${SKIP_TYPES.includes(row.type) ? "status-inactive" : "status-pending"}`} data-testid={`closure-type-${row.id}`} title={row.type_label || row.type}>{row.type}</span></td>
                      <td>{row.description || <span className="pairing-meta">—</span>}{row.note && <><br /><small className="pairing-meta">{row.note}</small></>}</td>
                      <td><small className="pairing-meta">{row.source}</small></td>
                      {canManage && (
                        <td className="closure-actions">
                          <button type="button" className="provider-profile-link" data-testid={`closure-edit-${row.id}`} onClick={() => setEditing(row)}><Pencil size={13} /> Edit</button>
                          <button type="button" className="provider-profile-link" data-testid={`closure-delete-${row.id}`} onClick={() => remove(row)}><Trash2 size={13} /> Delete</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {closures && !rows.length && <tr><td colSpan={canManage ? 6 : 5} className="provider-empty-state" data-testid="closures-empty">No closures on file yet{canManage ? " — add one or import the CSV" : ""}.</td></tr>}
                  {!closures && !error && <tr><td colSpan={6} className="provider-empty-state">Loading…</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
